import React, { useState } from 'react';
import ResidentSidebar from '../components/ResidentSidebar';
import buildingImg from '../assets/Polderzicht1.jpeg';
import '../css/OpnamestaatHandleiding.css';

// Public (niet-gelinkte) handleiding voor bewoners over de online opnamestaat.
// Route: /handleiding-opnamestaat
// Tweetalig (NL/EN) — schakelt mee met de taalknop in de ResidentSidebar.
//
// Screenshots komen uit public/manual-screenshots/ (gevuld door
// `npm run build:manual`). Ontbreken ze, dan tonen we een vriendelijke
// placeholder zodat de pagina altijd werkt.

const CONTENT = {
  nl: {
    heroTitle: 'Online Opnamestaat',
    heroSubtitle: 'Handleiding voor bewoners',
    heroBadge: 'Huisnummers 211 t/m 305',
    pdfButton: '📄 Opslaan als PDF',
    pdfTitle: 'Opslaan of afdrukken als PDF',

    bannerTitle: 'Doe dit op uw telefoon',
    bannerText: "U loopt straks even door uw woning en maakt onderweg een paar foto's met de camera — voordeur, balkon, badkamer. Dat gaat het makkelijkst op uw mobiel. Leest u de uitnodiging op uw computer? Scan dan de QR-code in de e-mail, of stuur de link naar uzelf, en open hem op uw telefoon.",

    introTitle: 'Beste bewoner,',
    introP1: 'Fijn dat u meedoet! Als Vereniging van Eigenaren willen we ons complex goed onderhouden — nu én voor de komende jaren. Daarvoor hebben we per appartement een actueel beeld nodig van de staat van de kozijnen, het balkon, de voordeur en de installaties binnen.',
    introP2: 'Tot nu toe deden we dat met afspraken op locatie. Dat kost veel tijd, zowel voor u als voor de opnemer. Daarom bieden we u nu een <strong>online opnamestaat</strong> aan: u vult zelf, op een moment dat het u uitkomt, de staat van uw woning in. Dat duurt ongeveer <strong>30 minuten</strong> en kan in etappes — tussentijds stoppen en later verder gaan mag.',
    whyTitle: 'Waarom doen we dit?',
    why: [
      '<strong>Uw appartement goed in beeld</strong> — zo kunnen we onderhoud plannen dat écht nodig is.',
      '<strong>Meerjarenonderhoudsplan (MJOP)</strong> — de gegevens voeden direct ons plan voor de komende jaren.',
      '<strong>Uw tijd</strong> — geen afspraak inplannen, geen opnemer thuis ontvangen.',
      '<strong>Uw stem telt</strong> — u kunt wensen, aanpassingen en opmerkingen direct meegeven.'
    ],

    needTitle: 'Wat heeft u nodig?',
    need: [
      "Uw <strong>telefoon</strong> met internet — daarmee maakt u onderweg meteen de foto's. Een tablet kan ook.",
      'Uw persoonlijke <strong>uitnodigingslink</strong> uit onze e-mail (geen wachtwoord nodig).',
      'Ongeveer <strong>30 minuten</strong> aaneengesloten — of verspreid over meerdere momenten.',
      "Zin om een paar <strong>foto's</strong> te maken van voordeur, ramen, balkon en installaties."
    ],

    stepsTitle: 'Zo werkt het — stap voor stap',
    steps: [
      { n: 1, title: 'Open uw uitnodigingslink', text: 'U ontvangt van VvE Polderzicht een e-mail met een persoonlijke link. Tik op die link om de opnamestaat te openen. U hoeft niet in te loggen — de link is uniek voor uw adres.', img: 'welkom.png' },
      { n: 2, title: 'Voordeur', text: 'Beoordeel uw voordeur op hout, schilderwerk en glas. U gebruikt een sterren-beoordeling van 1 (slecht) tot 5 (uitstekend). Weet u het niet zeker? Kies wat het dichtst in de buurt komt — het hoeft niet perfect te zijn.', img: 'voordeur.png', tip: "Maak één of twee foto's van uw voordeur, ook als deze er goed uitziet. Dat helpt ons enorm." },
      { n: 3, title: 'Ramen (kantel- en kiepramen)', text: 'Beoordeel de ramen in slaapkamer en woonkamer. Per raam noteert u de staat van tochtafdichting, scharnieren en sluitwerk, plus eventuele opmerkingen.', img: 'ramen.png' },
      { n: 4, title: 'Balkon klein', text: 'Voor het kleine balkon beoordeelt u hout, schilderwerk, glas, beton en balustrade.', img: 'balkonKlein.png' },
      { n: 5, title: 'Balkon groot', text: 'Bij het grote balkon beoordeelt u dezelfde onderdelen én geeft u aan wat er op het balkon staat (meubilair, planten, stroomvoorziening).', img: 'balkonGroot.png' },
      { n: 6, title: 'Installaties', text: 'We lopen langs de installaties en apparaten in uw woning (zoals de boiler en aansluitingen). U kunt bij elk onderdeel een opmerking toevoegen.', img: 'installaties.png' },
      { n: 7, title: 'Ventilatie, verwarming & afvoer', text: 'Geef de staat door van ventilatie, verwarming en de afvoeren in uw woning. Weet u iets niet zeker? Laat het op de standaardwaarde staan en voeg eventueel een opmerking toe.', img: 'ventilatie.png' },
      { n: 8, title: "Opmerkingen & foto's", text: "Tot slot ruimte voor eventuele gebreken, opmerkingen, wensen en extra foto's of documenten (offertes, rapporten).", img: 'opmerkingen.png' },
      { n: 9, title: 'Controleren & indienen', text: 'Op de laatste pagina ziet u een overzicht. Controleer uw antwoorden rustig en klik op "Indienen". U ontvangt daarna een bevestigingsmail. De beheerder bekijkt uw opnamestaat en neemt eventueel contact op bij vragen.', img: 'review.png' }
    ],

    photosTitle: "Foto's toevoegen",
    photosP: 'Op veel stappen kunt u foto\'s uploaden. Tik op de knop <em>"Foto toevoegen"</em>, kies een bestand (JPG, PNG of PDF, max 10 MB) en de foto wordt direct opgeslagen. U kunt per onderdeel meerdere foto\'s toevoegen.',
    photosTip: 'foto\'s van detailschade (een scheur, afbladderende verf, een kapotte klink) zijn veel waardevoller dan overzichtsfoto\'s.',

    saveTitle: 'Tussentijds stoppen? Geen probleem.',
    saveP: 'Uw antwoorden worden <strong>automatisch opgeslagen</strong>, elke paar seconden. U kunt het tabblad rustig sluiten en later op dezelfde link terugkomen — u gaat verder waar u gebleven was.',

    faqTitle: 'Veelgestelde vragen',
    faq: [
      { q: 'Moet ik overal een sterren-beoordeling geven?', a: 'Liever wel, maar als u iets echt niet kunt beoordelen, laat het dan op de standaardwaarde staan en schrijf een opmerking.' },
      { q: 'Ik heb geen klein balkon (of geen groot balkon). Wat doe ik?', a: 'Laat de waarden gewoon leeg of zet een opmerking "niet van toepassing". De beheerder begrijpt dit.' },
      { q: 'Kan ik de link delen met mijn partner?', a: 'Ja, de link is gekoppeld aan uw huisnummer. U kunt dus samen invullen.' },
      { q: 'Mijn link werkt niet meer.', a: 'Links zijn 30 dagen geldig. Is deze verlopen? Mail ons dan, dan sturen we een nieuwe.' },
      { q: 'Kan iemand anders mijn antwoorden zien?', a: 'Alleen het bestuur en de beheerder. Uw antwoorden worden niet met andere bewoners gedeeld.' }
    ],

    contactTitle: 'Hulp nodig?',
    contactP1: 'Mail ons op <a href="mailto:bestuurpolderzicht@gmail.com">bestuurpolderzicht@gmail.com</a> — we helpen graag.',
    contactP2: 'Komt u er online niet uit? Dan plannen we alsnog een afspraak op locatie in.',
    signoff: 'Bedankt dat u meewerkt aan het goed onderhouden van ons complex.<br/>— Het bestuur van VvE Polderzicht',

    tipLabel: 'Tip:',
    placeholder: 'Voorbeeld-afbeelding wordt toegevoegd zodra de handleiding wordt gepubliceerd.',
    zoomHint: '🔍 Tik om te vergroten',
    zoomTitle: 'Klik om te vergroten',
    lightboxLabel: 'Afbeelding vergroot',
    lightboxClose: 'Sluiten',
    lightboxHint: 'Knijp om in te zoomen · tik buiten de afbeelding om te sluiten'
  },

  en: {
    heroTitle: 'Online Maintenance Check',
    heroSubtitle: 'Guide for residents',
    heroBadge: 'House numbers 211–305',
    pdfButton: '📄 Save as PDF',
    pdfTitle: 'Save or print as PDF',

    bannerTitle: 'Do this on your phone',
    bannerText: "You'll walk through your home and take a few photos with the camera along the way — front door, balcony, bathroom. That's easiest on your mobile. Reading the invitation on your computer? Scan the QR code in the email, or send the link to yourself, and open it on your phone.",

    introTitle: 'Dear resident,',
    introP1: "Great that you're taking part! As an Owners' Association we want to keep our complex in good shape — now and for the years ahead. For that we need an up-to-date picture, per apartment, of the condition of the window frames, the balcony, the front door and the installations inside.",
    introP2: 'Until now we did this with on-site appointments. That takes a lot of time, both for you and for the surveyor. So we now offer you an <strong>online maintenance check</strong>: you record the condition of your home yourself, whenever it suits you. It takes about <strong>30 minutes</strong> and can be done in stages — stop in between and continue later.',
    whyTitle: 'Why are we doing this?',
    why: [
      '<strong>Your apartment clearly in view</strong> — so we can plan maintenance that is really needed.',
      '<strong>Multi-year maintenance plan (MJOP)</strong> — the data feeds directly into our plan for the coming years.',
      '<strong>Your time</strong> — no appointment to schedule, no surveyor to receive at home.',
      '<strong>Your voice counts</strong> — you can pass on wishes, changes and comments directly.'
    ],

    needTitle: 'What do you need?',
    need: [
      'Your <strong>phone</strong> with internet — that is how you take the photos along the way. A tablet works too.',
      'Your personal <strong>invitation link</strong> from our email (no password needed).',
      'About <strong>30 minutes</strong> in one go — or spread across several moments.',
      'A willingness to take a few <strong>photos</strong> of the front door, windows, balcony and installations.'
    ],

    stepsTitle: 'How it works — step by step',
    steps: [
      { n: 1, title: 'Open your invitation link', text: "You'll receive an email from VvE Polderzicht with a personal link. Tap it to open the check. No login needed — the link is unique to your address.", img: 'welkom.png' },
      { n: 2, title: 'Front door', text: "Rate your front door on wood, paintwork and glass, using a 1 (poor) to 5 (excellent) star rating. Not sure? Pick whatever's closest — it doesn't have to be perfect.", img: 'voordeur.png', tip: 'Take one or two photos of your front door, even if it looks fine. That helps us a lot.' },
      { n: 3, title: 'Windows (tilt-and-turn)', text: 'Rate the windows in the bedroom and living room. For each window, note the state of the draught sealing, hinges and locks, plus any comments.', img: 'ramen.png' },
      { n: 4, title: 'Small balcony', text: 'For the small balcony, rate the wood, paintwork, glass, concrete and balustrade.', img: 'balkonKlein.png' },
      { n: 5, title: 'Large balcony', text: "For the large balcony, rate the same parts and tell us what's on the balcony (furniture, plants, power supply).", img: 'balkonGroot.png' },
      { n: 6, title: 'Installations', text: 'We go through the installations and appliances in your home (such as the boiler and connections). You can add a comment to each part.', img: 'installaties.png' },
      { n: 7, title: 'Ventilation, heating & drainage', text: 'Tell us the state of the ventilation, heating and drains in your home. Not sure about something? Leave it at the default value and add a comment if you like.', img: 'ventilatie.png' },
      { n: 8, title: 'Comments & photos', text: 'Finally, room for any defects, comments, wishes and extra photos or documents (quotes, reports).', img: 'opmerkingen.png' },
      { n: 9, title: 'Review & submit', text: 'On the last page you\'ll see an overview. Check your answers at your leisure and click "Submit". You\'ll then receive a confirmation email. The administrator reviews your check and may contact you with any questions.', img: 'review.png' }
    ],

    photosTitle: 'Adding photos',
    photosP: 'On many steps you can upload photos. Tap the <em>"Add photo"</em> button, choose a file (JPG, PNG or PDF, max 10 MB) and the photo is saved straight away. You can add several photos per part.',
    photosTip: 'photos of specific damage (a crack, flaking paint, a broken handle) are far more valuable than overview photos.',

    saveTitle: 'Need to stop halfway? No problem.',
    saveP: 'Your answers are <strong>saved automatically</strong>, every few seconds. You can close the tab and come back later on the same link — you continue right where you left off.',

    faqTitle: 'Frequently asked questions',
    faq: [
      { q: 'Do I have to give a star rating everywhere?', a: "Preferably yes, but if you really can't assess something, leave it at the default value and write a comment." },
      { q: "I don't have a small balcony (or a large balcony). What do I do?", a: 'Just leave the values blank or add a comment "not applicable". The administrator will understand.' },
      { q: 'Can I share the link with my partner?', a: 'Yes, the link is tied to your house number. So you can fill it in together.' },
      { q: 'My link no longer works.', a: "Links are valid for 30 days. Expired? Email us and we'll send a new one." },
      { q: 'Can anyone else see my answers?', a: 'Only the board and the administrator. Your answers are not shared with other residents.' }
    ],

    contactTitle: 'Need help?',
    contactP1: "Email us at <a href=\"mailto:bestuurpolderzicht@gmail.com\">bestuurpolderzicht@gmail.com</a> — we're happy to help.",
    contactP2: "Can't manage online? We'll still schedule an on-site appointment.",
    signoff: 'Thank you for helping keep our complex in good shape.<br/>— The board of VvE Polderzicht',

    tipLabel: 'Tip:',
    placeholder: 'Example image will be added once the guide is published.',
    zoomHint: '🔍 Tap to enlarge',
    zoomTitle: 'Click to enlarge',
    lightboxLabel: 'Image enlarged',
    lightboxClose: 'Close',
    lightboxHint: 'Pinch to zoom · tap outside the image to close'
  }
};

const Screenshot = ({ file, alt, onZoom, t }) => {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div className="step-screenshot placeholder">
        {t.placeholder}
      </div>
    );
  }
  const src = `/manual-screenshots/${file}`;
  return (
    <div className="step-screenshot" onClick={() => onZoom(src, alt)} title={t.zoomTitle}>
      <img src={src} alt={alt} onError={() => setFailed(true)} />
      <div className="zoom-hint">{t.zoomHint}</div>
    </div>
  );
};

const Lightbox = ({ src, alt, onClose, t }) => {
  if (!src) return null;
  return (
    <div className="lightbox" onClick={onClose} role="dialog" aria-label={t.lightboxLabel}>
      <button className="lightbox__close" onClick={onClose} aria-label={t.lightboxClose}>×</button>
      <div className="lightbox__scroll" onClick={(e) => e.stopPropagation()}>
        <img src={src} alt={alt} />
      </div>
      <div className="lightbox__hint">{t.lightboxHint}</div>
    </div>
  );
};

const OpnamestaatHandleiding = () => {
  const [language, setLanguage] = useState(localStorage.getItem('language') || 'nl');
  const [zoom, setZoom] = useState(null);
  const openZoom = (src, alt) => setZoom({ src, alt });
  const closeZoom = () => setZoom(null);

  const t = CONTENT[language] || CONTENT.nl;

  React.useEffect(() => {
    if (!zoom) return;
    const onKey = (e) => { if (e.key === 'Escape') closeZoom(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [zoom]);

  return (
    <div className="page-with-sidebar handleiding-page">
      <ResidentSidebar language={language} setLanguage={setLanguage} />

      <div className="main-content">
        <div className="handleiding-container">

          {/* Hero */}
          <div className="handleiding-hero">
            <img className="handleiding-hero__img" src={buildingImg} alt="VvE Polderzicht" />
            <div className="handleiding-hero__overlay">
              <h1>{t.heroTitle}</h1>
              <p className="subtitle">{t.heroSubtitle}</p>
              <span className="handleiding-hero__badge">{t.heroBadge}</span>
            </div>
          </div>

          {/* Print button */}
          <div className="handleiding-actions">
            <button onClick={() => window.print()} title={t.pdfTitle}>
              {t.pdfButton}
            </button>
          </div>

          {/* Telefoon-banner — de opname is bedoeld voor de mobiel */}
          <div className="phone-banner">
            <span className="phone-banner__icon" aria-hidden="true">📱</span>
            <div>
              <h2>{t.bannerTitle}</h2>
              <p>{t.bannerText}</p>
            </div>
          </div>

          {/* Intro */}
          <div className="handleiding-card">
            <h2>{t.introTitle}</h2>
            <p dangerouslySetInnerHTML={{ __html: t.introP1 }} />
            <p dangerouslySetInnerHTML={{ __html: t.introP2 }} />

            <h3>{t.whyTitle}</h3>
            <ul className="why-list">
              {t.why.map((item, i) => (
                <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
              ))}
            </ul>
          </div>

          {/* What you need */}
          <div className="handleiding-card">
            <h2>{t.needTitle}</h2>
            <ul>
              {t.need.map((item, i) => (
                <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
              ))}
            </ul>
            <div className="tip">
              <strong>{t.tipLabel}</strong> {language === 'en'
                ? 'do the walk-through with your phone in hand, so you can take photos of the front door, windows, balcony and installations straight away.'
                : 'doe de rondgang met uw telefoon in de hand, zodat u direct foto\'s kunt maken van voordeur, ramen, balkon en installaties.'}
            </div>
          </div>

          {/* Steps */}
          <h2 style={{ color: '#246834', margin: '30px 0 14px 4px' }}>{t.stepsTitle}</h2>
          {t.steps.map(s => (
            <div className="step-card" key={s.n}>
              <div className="step-number">{s.n}</div>
              <div className="step-body">
                <h2>{s.title}</h2>
                <p>{s.text}</p>
                {s.tip && <div className="tip"><strong>{t.tipLabel}</strong> {s.tip}</div>}
                <Screenshot file={s.img} alt={s.title} onZoom={openZoom} t={t} />
              </div>
            </div>
          ))}

          {/* Photos */}
          <div className="handleiding-card">
            <h2>{t.photosTitle}</h2>
            <p dangerouslySetInnerHTML={{ __html: t.photosP }} />
            <div className="tip">
              <strong>{t.tipLabel}</strong> {t.photosTip}
            </div>
          </div>

          {/* Save & resume */}
          <div className="handleiding-card">
            <h2>{t.saveTitle}</h2>
            <p dangerouslySetInnerHTML={{ __html: t.saveP }} />
          </div>

          {/* FAQ */}
          <div className="handleiding-card">
            <h2>{t.faqTitle}</h2>
            {t.faq.map((f, i) => (
              <div className="faq-item" key={i}>
                <h4>{f.q}</h4>
                <p>{f.a}</p>
              </div>
            ))}
          </div>

          {/* Contact */}
          <div className="handleiding-contact">
            <h2>{t.contactTitle}</h2>
            <p dangerouslySetInnerHTML={{ __html: t.contactP1 }} />
            <p>{t.contactP2}</p>
          </div>

          <p className="handleiding-signoff" dangerouslySetInnerHTML={{ __html: t.signoff }} />

        </div>

        {zoom && <Lightbox src={zoom.src} alt={zoom.alt} onClose={closeZoom} t={t} />}

        <footer className="page-footer">
          <p>&copy; {new Date().getFullYear()} VVE Polderzicht • <a href="mailto:bestuurpolderzicht@gmail.com">bestuurpolderzicht@gmail.com</a></p>
        </footer>
      </div>
    </div>
  );
};

export default OpnamestaatHandleiding;
